<!--

    The list needs a configuration object for the list itself and a filter object for the sorting and filtering.
        - SimpleListListFilter
        - SimpleListConfiguration

    SimpleListListFilter:

        The simple list filter is used to create the table header and the possible filters for the given DefaultSimple list.default

        For more information Take a look a the coments on the SimpleListListFilter

    Endlessscroll:
        If the SimpleListListFilter.size property is set, it will automatically add an endless scroll listener.

    DisplayMode:
        The list itself does know about a ViewMode. If ViewMode.LIST is set, it will use the internal DefaultSimpleListTableView otherwise it uses
        DefaultSimpleListCardView.

        Look in the respectiv components for more information.
    
    Local / Web Sorting:
        If the FilterOption.sorting function is set on a FilterOption, it will use the internal function to sort the
        items localy. Be carefull, it will sort the data array itself. If no function is given, it will dispatch an event,
        that the sorting has changed.

    Selection:
        The list iteself will use a writeable to remember the last selected item. It will as well set a flag on all the object which are selected.
        That's why it's recomended to add the MultiSelectionItem interface to the item interface.

    Events:
        - selectionChanged: Will inform if the selection was changed. The event datail will hold the clicked item. In case of multiselction this is only the last item.
        - sortingChanged: Informs that the user clicked on a header and we should update the sorting acordingly. This is only called when the sorting is not local.
        - loadMore: We reached the bottom of the list. Is only actived if page limit is given.
        - customEvent: Special states that needs to emited by the list item.

    Item component:
        - The child receice the item as "item" property. So make sure in the <script> section you use:
            - export let item: YOUR_CLASS
        - The child will as well recieve the ViewMode property to change accordingly.
        - The child will get the selected property for easy ref. This will check the local store.

-->
<script lang="ts">
	import { onMount, setContext } from 'svelte';
	import IconButton from '../buttons/IconButton.svelte';
	import { contextMenuState } from '../contextmenu/ContextMenuStore';
	import {
		saveListSettingsToLocalStorage,
		SortingOrder,
		ViewMode,
		type FilterOption,
		type ListItemInfo,
		type MultiSelectionItem,
		type SimpleListStore
	} from '../../types/ListTypes';
	import DefaultSimpleListCardView from './parts/DefaultSimpleListCardView.svelte';
	import SimpleListTableView from './parts/DefaultSimpleListTableView.svelte';

	//Object related to filtering and sorting. It will create the sorting options and the filter options

	interface Props {
		//The svelte component used a child.
		itemComponent: any;
		//The id for the scrolling part of the list. Can be used to scroll to top via document.getElementById(id).scrollTop = 0
		id?: string;
		store: SimpleListStore;
		onDblClick?: (item: MultiSelectionItem) => void;
		onSelectionChanged?: (item: MultiSelectionItem) => void;
		onMultiSelectionChanged?: () => void;
		onCustomEvent?: (e: any) => void;
		onDragStartRequest?: (info: ListItemInfo) => void;
		onRequestContextmenu?: (info: ListItemInfo) => void;
		onAddRequest?: (index: number) => void;
		onDrop?: () => void;
		onChildDeleted?: (item: MultiSelectionItem) => void;
		onEditRequest?: (item: MultiSelectionItem) => void;
	}

	let {
		itemComponent,
		id = '',
		store = $bindable(),
		onDblClick = undefined,
		onCustomEvent = undefined,
		onDragStartRequest = undefined,
		onRequestContextmenu = undefined,
		onAddRequest = undefined,
		onDrop = undefined,
		onSelectionChanged = undefined,
		onChildDeleted = undefined,
		onMultiSelectionChanged = undefined,
		onEditRequest = undefined
	}: Props = $props();

	//Keep track of how many items are selected
	let selectedItemCount = $derived(store.data.filter((item) => item.selected).length);

	//Remember the last selection index if only one item was selected
	let singleSelectIndex: number | null = null;

	//Find the first sorting which is available. Incase we do not have any sorting loaded after
	let activeSorting: FilterOption | null = $derived(
		store.listFilter.filterOptions.find((opt) => opt.isSorting == null || opt.isSorting) || null
	);

	onMount(() => {
		if (store.listFilter.localStorageKey != null) {
			const viewModeRaw = window.localStorage.getItem(
				`${store.listFilter.localStorageKey}-viewMode`
			);

			if (viewModeRaw != null) {
				let viewMode: ViewMode = (<any>ViewMode)[viewModeRaw];

				if (viewMode == undefined) {
					viewMode = ViewMode.SMALL;
				}

				if (Object.values(ViewMode).includes(viewMode)) {
					store.listConfig.viewMode = viewMode;
				}
			}
		}
	});

	function interceptContextmenu(info: ListItemInfo) {
		if (selectedItemCount <= 1) {
			handleSelection(info);
		}
		onRequestContextmenu?.(info);
	}

	function handleSelection(info: ListItemInfo) {
		//Do we have the single select toggle activated?
		if (store.listConfig.singleSelectToggle) {
			//Toggle the selection
			info.item.selected = !info.item.selected;

			onMultiSelectionChanged?.();
		} else {
			//Set the default start index to the item index
			let startIndex = info.index;
			let endIndex = info.index;

			if (info.event.shiftKey && store.listConfig.allowMultiSelection) {
				//Get the start
				startIndex = singleSelectIndex != null ? singleSelectIndex : startIndex;

				//If end is less than start swap
				endIndex = info.index > startIndex ? info.index : startIndex;
				//We have to check the start index after end
				startIndex = info.index < startIndex ? info.index : startIndex;
			} else {
				//Set the new single select index
				singleSelectIndex = startIndex;
			}

			for (let i = 0; i < store.data.length; i++) {
				const dataItem = store.data[i];

				if ((info.event.ctrlKey || info.event.metaKey) && store.listConfig.allowMultiSelection) {
					if (i == startIndex) {
						dataItem.selected = !dataItem.selected;
					}
				} else {
					//Set the selection depending on the index
					dataItem.selected = i >= startIndex && i <= endIndex;
				}
			}

			if (startIndex == endIndex) {
				//And dispach an event for custom handling
				onSelectionChanged?.(info.item);
			} else {
				onMultiSelectionChanged?.();
			}
		}

		//Make sure that we hide the context menu
		$contextMenuState = null;
	}

	function handleChangeSortOrder(option: FilterOption) {
		//Get the current sort mode. It can be undefined, since the programmer can omit the option.
		const currentSortMode = store.listFilter.sortingOrder || SortingOrder.ASC;

		switch (currentSortMode) {
			case SortingOrder.ASC:
				store.listFilter.sortingOrder = SortingOrder.DESC;
				break;
			case null:
			case SortingOrder.DESC:
				store.listFilter.sortingOrder = SortingOrder.ASC;
				break;
		}

		if (option.sort == null) {
			//Load the data again
			store.applyFilter();
		} else {
			orderDataLocal(option);
		}
	}

	function orderDataLocal(option: FilterOption) {
		//Should we sort des or asc
		let modifier = store.listFilter.sortingOrder == SortingOrder.DESC ? -1 : 1;

		//Reasign so that svelete renders the list again.
		store.data = store.data.toSorted((a, b) => {
			let valueA = option.sort!(a);
			let valueB = option.sort!(b);

			if (valueA == null) {
				valueA = '';
			}

			if (valueB == null) {
				valueB = '';
			}

			if (typeof valueA != 'number' || typeof valueB != 'number') {
				return (valueA + '').localeCompare(valueB + '') * modifier;
			} else {
				return (valueA - valueB) * modifier;
			}
		});
	}

	function handleSorting(option: FilterOption) {
		if (store.listFilter.size == 0 && option.sort == null) {
			return;
		}

		//If we have the same sorting already, change to order.
		if (activeSorting == option) {
			handleChangeSortOrder(option);
		} else {
			//Set the current sorting.
			activeSorting = option;

			//Set the value
			store.listFilter.sortingKey = option.key;

			if (option.sort == null) {
				//Load the data again
				store.applyFilter();
			} else {
				orderDataLocal(option);
			}
		}

		//Save the order to local storage
		saveListSettingsToLocalStorage(store.listFilter);
	}

	/**
	 * Let the list emitt a filter change event. This will be triggered by the filters
	 * via getContext
	 */
	function updateFilter() {
		//Save the value
		saveListSettingsToLocalStorage(store.listFilter);
		//Inform components outside
		store.applyFilter();
	}

	/**
	 * Get the icon name for displaying the button on the right top corner.
	 */
	function getIconNameForViewMode(viewMode: ViewMode): string {
		switch (viewMode) {
			case ViewMode.LIST:
				return 'list';
			case ViewMode.BIG:
				return 'grid-2';
			default:
				return 'grid';
		}
	}

	/**
	 * Set the view mode to the list.
	 */
	function setViewMode(viewMode: ViewMode) {
		store.listConfig.viewMode = viewMode;

		//Save the view mode if we have given a name to the list
		if (store.listFilter.localStorageKey != null) {
			window.localStorage.setItem(`${store.listFilter.localStorageKey}-viewMode`, viewMode);
		}
	}

	/**
	 * Load the active sorting from the filter.
	 */
	function restoreActiveSortingFromFilter() {
		//Set the active sorting
		activeSorting =
			store.listFilter.filterOptions.find((option) => option.key == store.listFilter.sortingKey) ||
			null;

		//If we have a local sorting function start the local sort
		if (activeSorting?.sort) {
			orderDataLocal(activeSorting);
		}
	}

	//We need to get the active sorting from the filter.
	restoreActiveSortingFromFilter();

	setContext('enzianListContext', {
		updateFilterActivityState: updateFilter
	});
</script>

<div class="h-full flex flex-col" class:overflow-y-auto={store.listConfig.growHeight}>
	<div class="grid grid-cols-[1fr_auto] relative">
		<!--Header-->
		{#if store.listConfig.header}
			<p class="text-xl font-bold mb-8 pl-8 uppercase">
				{store.listConfig.header}
			</p>
		{:else}
			<p></p>
		{/if}

		{#if store.listConfig.showViewModeSwitches.length > 0}
			<!--View-Modes-->
			<div class="flex justify-end items-end gap-2 mb-2 mr-4">
				{#each store.listConfig.showViewModeSwitches as viewMode}
					<IconButton
						primary={store.listConfig.viewMode === viewMode}
						size="small"
						iconName={getIconNameForViewMode(viewMode)}
						onClick={() => {
							setViewMode(viewMode);
							store.listConfig.viewMode = viewMode;
						}}
					/>
				{/each}
			</div>
		{:else}
			<p></p>
		{/if}
	</div>

	{#if store.listConfig.viewMode == ViewMode.LIST}
		<SimpleListTableView
			{id}
			{itemComponent}
			{selectedItemCount}
			bind:store
			onAddRequest={(index) => onAddRequest?.(index)}
			onDragStartRequest={(info) => onDragStartRequest?.(info)}
			onRequestContextmenu={interceptContextmenu}
			onCustomEvent={(e) => onCustomEvent?.(e)}
			onTableHeaderVisibilityChange={() => {
				saveListSettingsToLocalStorage(store.listFilter);
			}}
			onHeaderResized={() => {
				saveListSettingsToLocalStorage(store.listFilter);
			}}
			onChangeSortOrder={(header) => {
				handleSorting(header);
			}}
			onDblClick={(item) => {
				onDblClick?.(item);
			}}
			onSelection={handleSelection}
			{onChildDeleted}
			{onEditRequest}
		/>
	{:else}
		<DefaultSimpleListCardView
			{id}
			{activeSorting}
			{itemComponent}
			bind:store
			{selectedItemCount}
			onAddRequest={(index) => onAddRequest?.(index)}
			onDragStartRequest={(info: ListItemInfo) => onDragStartRequest?.(info)}
			onRequestContextmenu={interceptContextmenu}
			onDrop={() => onDrop?.()}
			onChangeSortOrder={(filter) => {
				handleSorting(filter);
			}}
			onCustomEvent={(e) => onCustomEvent?.(e)}
			onDblClick={(item) => {
				onDblClick?.(item);
			}}
			onSelection={handleSelection}
			{onChildDeleted}
			{onEditRequest}
		/>
	{/if}
</div>
